import RevealWrapper from "../../base/RevealWrapper";
import { BUSINESS, AWARD_IMAGE } from "../../../mocks/siteData";

export interface WhyChooseContent {
  eyebrow: string;
  headline: string;
  items: { icon: string; title: string; description: string }[];
  image: string;
}

interface Props {
  content: WhyChooseContent;
}

const beforeAfterImages = [
  {
    src: "https://public.readdy.ai/ai/img_res/a2cac01a97cb226d1f703febe249be3c.jpg",
    caption: "Volume Lash Extensions",
  },
  {
    src: "https://readdy.ai/api/search-image?query=precision%20lash%20artist%20applying%20individual%20eyelash%20extension%20to%20client%20eye%20with%20professional%20tweezers%2C%20extreme%20close-up%20detail%20of%20skilled%20artisan%20hands%20and%20delicate%20placement%2C%20clean%20white%20luxury%20studio%20background%2C%20editorial%20beauty%20artistry%20photography%2C%20masterful%20precision%20craftsmanship&width=560&height=420&seq=why-transform-2-v2&orientation=landscape",
    caption: "Precision Application",
  },
  {
    src: "https://readdy.ai/api/search-image?query=beautiful%20confident%20radiant%20woman%20with%20perfect%20eyelash%20extensions%20smiling%20and%20looking%20forward%2C%20luxury%20beauty%20portrait%2C%20warm%20studio%20lighting%2C%20cream%20neutral%20background%2C%20glowing%20skin%20and%20stunning%20lashes%2C%20editorial%20luxury%20beauty%20brand%20photography%2C%20empowered%20and%20elegant&width=560&height=420&seq=why-transform-3-v2&orientation=landscape",
    caption: "Confident Results",
  },
];

export default function WhyChooseUs({ content }: Props) {
  const { eyebrow, headline, items, image } = content;

  return (
    <section id="why-choose-us" className="bg-cream-light overflow-hidden">
      {/* Main split section */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left content — 7 cols */}
          <div className="lg:col-span-7">
            <RevealWrapper direction="left">
              <span className="section-eyebrow">{eyebrow}</span>
              <span className="gold-divider" />
              <h2 className="section-heading text-4xl md:text-5xl lg:text-[3.5rem] mt-0 mb-3 leading-tight">
                {headline}
              </h2>
              <p className="font-sans text-sm text-charcoal/50 font-light leading-relaxed mb-10 max-w-md">
                We set the standard for precision, safety, and luxury beauty in Utah County — and our 4.9-star rating from over 901 clients proves it.
              </p>
            </RevealWrapper>

            {/* Feature items */}
            <div className="space-y-0">
              {items.map((item, i) => (
                <RevealWrapper key={i} direction="left" delay={i * 0.09}>
                  <div className={`group flex items-start gap-5 py-6 ${i < items.length - 1 ? "border-b border-cream-dark" : ""}`}>
                    <div className="w-12 h-12 flex items-center justify-center bg-charcoal-dark text-gold flex-shrink-0 group-hover:bg-gold group-hover:text-charcoal-dark transition-all duration-300 cursor-default">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <div>
                      <strong className="font-serif text-lg font-light text-charcoal-dark block mb-1.5 group-hover:text-gold transition-colors duration-300">
                        {item.title}
                      </strong>
                      <p className="font-sans text-sm text-charcoal/55 leading-relaxed font-light">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </RevealWrapper>
              ))}
            </div>

            <RevealWrapper delay={0.4}>
              <div className="pt-8 flex flex-wrap gap-3">
                <a
                  href={BUSINESS.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dark"
                >
                  Reserve Your Appointment
                  <i className="ri-arrow-right-line" />
                </a>
                <a
                  href={BUSINESS.reviewUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="btn-outline-dark"
                >
                  Read Reviews
                </a>
              </div>
            </RevealWrapper>
          </div>

          {/* Right image — 5 cols, full-height with floating cards */}
          <div className="lg:col-span-5">
            <RevealWrapper direction="right" delay={0.15}>
              <div className="relative">
                <div className="h-[520px] lg:h-[640px] overflow-hidden">
                  <img
                    src={image}
                    alt={`The Lash Bar — ${headline}`}
                    title={`The Lash Bar ${headline} — Lehi, Utah`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal-dark/25 to-transparent" />
                </div>

                {/* Floating award badge — bottom left overlap */}
                <div className="absolute -bottom-6 -left-4 bg-white p-4 shadow-none border border-cream-dark z-10">
                  <img
                    src={AWARD_IMAGE}
                    alt="Voted Utah's Best Lashes"
                    title="Voted Utah's Best Lashes Award"
                    className="h-16 w-auto object-contain"
                  />
                </div>

                {/* Floating review pill — top right */}
                <div className="absolute top-6 -right-4 bg-charcoal-dark border border-gold/20 px-5 py-4 z-10">
                  <div className="flex items-center gap-1 mb-1">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-gold text-xs" />
                    ))}
                  </div>
                  <div className="font-serif text-2xl text-gold font-light leading-none">4.9</div>
                  <div className="font-sans text-[10px] text-white/40 mt-0.5">901 Reviews</div>
                </div>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </div>

      {/* Transformation gallery — extends edge-to-edge */}
      <div className="mt-20 md:mt-24">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-6 md:px-10 mb-8 flex items-center justify-between flex-wrap gap-4">
            <div>
              <span className="section-eyebrow">The Lash Bar Difference</span>
              <h3 className="font-serif text-3xl md:text-4xl font-light text-charcoal-dark mt-3 leading-tight">
                Precision. Artistry. Confidence.
              </h3>
            </div>
            <a
              href={BUSINESS.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-dark"
            >
              Experience Luxury Lash Extensions
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </RevealWrapper>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 pb-0">
          {beforeAfterImages.map((item, i) => (
            <RevealWrapper key={i} delay={i * 0.12}>
              <div className="relative h-64 md:h-80 overflow-hidden group cursor-default">
                <img
                  src={item.src}
                  alt={`The Lash Bar — ${item.caption}`}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-charcoal-dark/30 group-hover:bg-charcoal-dark/15 transition-colors duration-400" />
                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/70 to-transparent">
                  <span className="font-sans text-xs tracking-[0.2em] uppercase text-white/75">
                    {item.caption}
                  </span>
                </div>
              </div>
            </RevealWrapper>
          ))}
        </div>
      </div>
    </section>
  );
}
